import { kcal } from '@/lib/utils';

const SIZE = 176;
const STROKE = 14;
const RADIUS = (SIZE - STROKE) / 2;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * Consumed against the daily budget.
 *
 * The arc is capped at a full turn so going over budget does not wrap around
 * and read as "nearly done"; the colour and the negative remaining number carry
 * the overshoot instead.
 */
export function BudgetRing({
  consumed,
  budget,
  pending,
}: {
  consumed: number;
  budget: number | null;
  pending: boolean;
}) {
  const ratio = budget && budget > 0 ? consumed / budget : 0;
  const over = ratio > 1;
  const offset = CIRCUMFERENCE * (1 - Math.min(ratio, 1));
  const remaining = budget == null ? null : budget - consumed;

  return (
    <div className="relative" style={{ width: SIZE, height: SIZE }}>
      <svg
        width={SIZE}
        height={SIZE}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        role="img"
        aria-label={
          budget == null
            ? `กินไปแล้ว ${kcal(consumed)} แคลอรี ยังไม่ได้ตั้งงบ`
            : `กินไปแล้ว ${kcal(consumed)} จากงบ ${kcal(budget)} แคลอรี`
        }
      >
        <circle
          cx={SIZE / 2}
          cy={SIZE / 2}
          r={RADIUS}
          fill="none"
          stroke="var(--color-border-subtle)"
          strokeWidth={STROKE}
        />
        {budget != null && !pending && (
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke={over ? 'var(--color-danger)' : 'var(--color-brand)'}
            strokeWidth={STROKE}
            strokeLinecap="round"
            strokeDasharray={CIRCUMFERENCE}
            strokeDashoffset={offset}
            // Start the arc at 12 o'clock rather than 3.
            transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
            className="transition-[stroke-dashoffset] duration-500"
          />
        )}
      </svg>

      <div className="absolute inset-0 grid place-content-center text-center">
        <p className="text-3xl font-semibold tabular-nums">{pending ? '—' : kcal(consumed)}</p>
        <p className="text-xs text-ink-muted">kcal วันนี้</p>
        {remaining != null && !pending && (
          <p className={`mt-1 text-xs tabular-nums ${over ? 'text-danger' : 'text-ink-muted'}`}>
            {over ? `เกิน ${kcal(Math.abs(remaining))}` : `เหลือ ${kcal(remaining)}`}
          </p>
        )}
      </div>
    </div>
  );
}
